import { convertToLocale } from "@/utils/money";

/**
 * An amount in the card's own currency. The service sends a currency code we
 * do not control, and an unknown one makes `Intl` throw: show the bare number
 * rather than lose the row.
 */
export function formatMoney(amount: number, currency: string): string {
  try {
    return convertToLocale({ amount, currency_code: currency });
  } catch {
    return amount.toFixed(2);
  }
}

/**
 * The time a message limit lifts, in the salon's own time zone (the browser's),
 * for the sentence "You can ask again from <time>". Just the clock time when
 * it is later today, with the day added when it is not. The service sends UTC
 * (`reset_at`, section 7.2); showing that would be a puzzle to a salon.
 *
 * Returns `null` for a value that is not a date, so the caller can fall back to
 * wording with no time in it.
 */
export function formatResetTime(
  resetAt: string,
  locale: string,
  now: Date = new Date()
): string | null {
  const reset = new Date(resetAt);
  if (Number.isNaN(reset.getTime())) return null;

  const sameDay =
    reset.getFullYear() === now.getFullYear() &&
    reset.getMonth() === now.getMonth() &&
    reset.getDate() === now.getDate();

  try {
    return new Intl.DateTimeFormat(
      locale,
      sameDay
        ? { hour: "numeric", minute: "2-digit" }
        : {
            weekday: "short",
            day: "numeric",
            month: "short",
            hour: "numeric",
            minute: "2-digit"
          }
    ).format(reset);
  } catch {
    return reset.toLocaleString();
  }
}
